import { useState, type ReactElement } from 'react';
import type { EmberPhoto } from '../api';
import type { ParagraphSlice } from '../format';
import { ImagePreview } from './ImagePreview';
import './EmberPhotos.css';

/**
 * The speaker's own photos in an Ember (2026-09-15 拍板). The Story Writer
 * places each under the paragraph it belongs to, by that paragraph's end
 * offset in the canonical text (`after_char`, the source_map ruler). Every
 * surface that reads an Ember's body draws them the same way: after the
 * paragraph, whole, never cropped — the person chose this picture — and a
 * tap opens the Ember's whole set in the shared viewer. An Ember with none
 * reads exactly as before.
 */

/** The photos that sit under this paragraph. */
export function photosUnder(photos: EmberPhoto[] | undefined, p: ParagraphSlice): EmberPhoto[] {
  return (photos ?? []).filter((ph) => ph.after_char >= p.start && ph.after_char <= p.end);
}

export function EmberPhotoRow({ photos, all, emberId }: {
  /** The photos under one paragraph, in body order. */
  photos: EmberPhoto[];
  /** Every photo of the Ember — the set the viewer moves through. */
  all: EmberPhoto[];
  emberId: string;
}): ReactElement | null {
  const [open, setOpen] = useState<string | null>(null);
  if (photos.length === 0) return null;
  const urls = all.map((ph) => ph.url);
  return (
    <>
      <div className={`ember-photos count-${Math.min(photos.length, 3)}`}>
        {photos.map((ph) => (
          <button
            type="button"
            className="ember-photo"
            key={ph.url}
            data-log="ember_photo_open"
            data-log-ember-id={emberId}
            onClick={() => setOpen(ph.url)}
            aria-label="View photo"
          >
            <img
              src={ph.url}
              alt=""
              loading="lazy"
              width={ph.width ?? undefined}
              height={ph.height ?? undefined}
            />
          </button>
        ))}
      </div>
      {open && (
        <ImagePreview
          src={open}
          images={urls}
          index={Math.max(0, urls.indexOf(open))}
          alt="Photo from this Ember"
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
